/**
 * Message placement for the mission chat (agion/Agion#2003).
 *
 * Every chat message shows in the conversation, in the activity sidebar, or in
 * both. placeMessage() holds that decision for every message kind in one switch
 * over ChatMessageKind: a kind added to the union without a placement here
 * fails `tsc`. 'sidebar' and 'both' say that the sidebar owns the message.
 *
 * Readers in chat-ui:
 * - conversationMessages(): the messages the conversation shows, live and
 *   after a history reload.
 * - activityMessages(): the messages the Activity sidebar shows
 *   (ExecutionLogPanel).
 * - opensActivitySidebar(): the kinds that open the Activity sidebar when they
 *   arrive (MissionChatWithArtifacts).
 * - mayShowInConversation(): the messages a follow-up run carries over
 *   (followUpTranscript.ts); the conversation then applies the full context.
 * - streamMessageKind(): the kind of a stream event whose browser payload is
 *   generated from Go (useStreamingMissionChat).
 * - placeAtDelivery(): where a message sent while the agent works goes when
 *   its delivered receipt arrives.
 *
 * Reader in admin-ui: the Mission Designer run console (ExecutionChat) reads
 * conversationMessages() for the tool results whose file cards it shows.
 *
 * Every switch over the kinds ends in unknownKind(), so a kind or a generated
 * message type added without a case fails `tsc` in the package and in both
 * apps, and a kind this build does not know never throws at runtime (G6).
 *
 * No React and no app state here: chat-ui and the admin-ui Mission Designer
 * share the module through @agion/mission-stream.
 */

import {
  MessageTypeConnectionRestored,
  MessageTypeDelegation,
  MessageTypeEscalationAnswerFrame,
  MessageTypeEscalationAnswerResult,
  MessageTypeGovernanceDecision,
  MessageTypeHITLResponse,
  MessageTypeMissionTrigger,
  MessageTypeOperatorEndUserTurnEvent,
  MessageTypeOperatorHoldEvent,
  MessageTypeOperatorMessage,
  MessageTypeOperatorMessageReceipt,
  MessageTypeOperatorTurnEvent,
  MessageTypeQuestionAnswerResult,
  MessageTypeQuestionClosed,
  MessageTypeToolCall,
  MessageTypeToolResult,
  MessageTypeUserMessage,
  MessageTypeUserMessageQueued,
  MessageTypeUserMessageReceipt,
  type MissionStreamMessageType,
} from './generated/missionstream'
import { toolResultFiles } from './tool-output-files'

/**
 * The kinds of message the mission chat holds. chat-ui turns each stream event
 * and each stored history row it shows into one of these.
 */
export type ChatMessageKind =
  | 'agent_turn'
  | 'tool_call'
  | 'tool_result'
  | 'hitl_request'
  | 'hitl_response'
  | 'node_status'
  | 'transcript'
  | 'mission_trigger'
  | 'mission_completed'
  | 'mission_failed'
  | 'governance_event'
  | 'orchestrator_decision'
  | 'delegation'
  | 'delegation_result'
  | 'agent_thinking'
  | 'system_notification'
  | 'operator_turn'

/**
 * Ends a switch over every ChatMessageKind, generated message type or
 * delivery state (G6, M7): a value without a case reaches this call with a
 * type other than never, and `tsc` fails. At runtime a value this build does
 * not know gets `fallback`, and nothing throws.
 */
export function unknownKind<T>(_kind: never, fallback: T): T {
  return fallback
}

/**
 * The chat message kind of a stream event whose browser payload is generated
 * from the Go definition (generated/missionstream.ts): a view, or a frame the
 * relay forwards as the host sent it. A message type added to the generator
 * without a case here fails `tsc` in the package and in both apps.
 */
export function streamMessageKind(type: MissionStreamMessageType): ChatMessageKind {
  switch (type) {
    case MessageTypeToolCall:
      return 'tool_call'
    case MessageTypeToolResult:
      return 'tool_result'
    // Forwarded as the host sent them since P5; chat-ui showed both live
    // under these kinds before any reached a browser.
    case MessageTypeDelegation:
      return 'delegation'
    case MessageTypeGovernanceDecision:
      return 'governance_event'
    // The parent mission names a sub-mission to continue with; chat-ui shows
    // it as a card with a link to the sub-mission.
    case MessageTypeMissionTrigger:
      return 'mission_trigger'
    // Results and closure target existing answer/question entities. This
    // classification supplies no handler and creates no conversation row.
    case MessageTypeHITLResponse:
    case MessageTypeQuestionAnswerResult:
      return 'hitl_response'
    // An escalation answer and the relay's content-free result
    // (agion/Agion#3427) target the escalation, as the question results
    // above target their question; neither creates a conversation row.
    case MessageTypeEscalationAnswerFrame:
    case MessageTypeEscalationAnswerResult:
      return 'hitl_response'
    case MessageTypeQuestionClosed:
      return 'hitl_request'
    // A message the user sent while the agent works (phase 6), and its
    // receipts, which only change that message's delivery. Its history row is
    // a user row, which the conversation reloads as a hitl_response, so it
    // shows where it was delivered, the same live and on reload. The host's
    // list of the user's messages that still wait, after a reload, shows each
    // one as the same user row (Mid-Eval P6-2).
    case MessageTypeUserMessage:
    case MessageTypeUserMessageReceipt:
    case MessageTypeUserMessageQueued:
      return 'hitl_response'
    // The server withdrew a provisional host-disconnect end (P-095
    // Amendment S04-5): a change of the run's state, which the apps map to
    // running. Like the question results above, it creates no row.
    case MessageTypeConnectionRestored:
      return 'node_status'
    // Operator conversation takeover (agion/Agion#3435): a reply a person
    // wrote is its own kind, so no reader can show it under the agent's
    // name; the operator's own frame and its receipt only change that reply.
    // The hold's state reaches the end user as a platform notice.
    case MessageTypeOperatorTurnEvent:
    case MessageTypeOperatorMessage:
    case MessageTypeOperatorMessageReceipt:
      return 'operator_turn'
    case MessageTypeOperatorHoldEvent:
      return 'system_notification'
    // An end-user message the runner delivered during a hold reaches the
    // operator as the user row it reloads as; the end user's own view
    // already shows it.
    case MessageTypeOperatorEndUserTurnEvent:
      return 'hitl_response'
    default:
      // An unknown type passes through as its own kind: placement puts it in
      // the Activity sidebar, which shows it as a generic row.
      return unknownKind(type, type)
  }
}

export type MessagePlacement = 'conversation' | 'sidebar' | 'both'

/** A chat message as placement reads it: its kind and its data. */
export interface PlacedMessage {
  type: ChatMessageKind
  data: Record<string, unknown>
}

/** Facts about the rest of the conversation that a placement depends on. */
export interface PlacementContext {
  /**
   * The conversation holds at least one hitl_request or hitl_response. The
   * agent's questions and the users' answers then carry the conversation, and
   * agent turns stay in the sidebar.
   */
  conversationHasHitl: boolean
  /**
   * This hitl_request replays an earlier request or stored row. The existing
   * name is retained for callers; equal prompt text alone is not a replay.
   */
  repeatsEarlierPrompt: boolean
}

/** Where one message shows. */
export function placeMessage(msg: PlacedMessage, ctx: PlacementContext): MessagePlacement {
  const type = msg.type
  switch (type) {
    // The agent's questions and messages to the user, and the users' answers.
    case 'hitl_request':
      return ctx.repeatsEarlierPrompt ? 'sidebar' : 'both'
    case 'hitl_response':
      return 'both'
    // Turns of a mission that asks no questions (simple question and answer):
    // their prompts and responses have no HITL message that could show them.
    case 'agent_turn':
      return ctx.conversationHasHitl ? 'sidebar' : 'both'
    // Policy notices, child missions, voice speech and chat notices. The
    // sidebar has no entry for them.
    case 'governance_event':
    case 'mission_trigger':
    case 'transcript':
    case 'system_notification':
    // A person's reply in an operator takeover.
    case 'operator_turn':
      return 'conversation'
    // A tool result that made files: the conversation shows their file cards
    // (agion/Agion#2003). Every other tool result is agent activity.
    case 'tool_result':
      return toolResultFiles(msg.data).length > 0 ? 'both' : 'sidebar'
    // Agent activity.
    case 'tool_call':
    case 'orchestrator_decision':
    case 'delegation':
    case 'delegation_result':
    case 'agent_thinking':
    case 'node_status':
      return 'sidebar'
    // Run endings. The conversation draws its completion or failure line from
    // the run state; the sidebar ends its live indicator on them and lists a
    // failure.
    case 'mission_completed':
    case 'mission_failed':
      return 'sidebar'
    default:
      // A kind the chat does not know stays out of the conversation, as every
      // unlisted kind did before the table existed; the Activity sidebar shows
      // it as a generic row.
      return unknownKind(type, 'sidebar')
  }
}

/**
 * Whether a message of this kind opens the Activity sidebar when it arrives
 * (chat-ui MissionChatWithArtifacts): the five kinds of agent work the
 * sidebar opened on before the list moved here.
 */
export function opensActivitySidebar(kind: ChatMessageKind): boolean {
  switch (kind) {
    case 'agent_turn':
    case 'tool_call':
    case 'node_status':
    case 'orchestrator_decision':
    case 'delegation':
      return true
    case 'hitl_request':
    case 'hitl_response':
    case 'tool_result':
    case 'delegation_result':
    case 'agent_thinking':
    case 'governance_event':
    case 'mission_trigger':
    case 'transcript':
    case 'system_notification':
    case 'operator_turn':
    case 'mission_completed':
    case 'mission_failed':
      return false
    default:
      return unknownKind(kind, false)
  }
}

function showsInConversation(placement: MessagePlacement): boolean {
  return placement !== 'sidebar'
}

function showsInSidebar(placement: MessagePlacement): boolean {
  return placement !== 'conversation'
}

/** The messages the conversation shows, in their order. */
export function conversationMessages<T extends PlacedMessage>(messages: readonly T[]): T[] {
  return placedMessages(messages, showsInConversation)
}

/**
 * The messages the Activity sidebar shows, in their order: every message the
 * sidebar owns, including a kind this build does not know.
 */
export function activityMessages<T extends PlacedMessage>(messages: readonly T[]): T[] {
  return placedMessages(messages, showsInSidebar)
}

/** The messages whose placement, with the whole list as context, passes `shows`. */
function placedMessages<T extends PlacedMessage>(
  messages: readonly T[],
  shows: (placement: MessagePlacement) => boolean,
): T[] {
  const conversationHasHitl = messages.some((m) => m.type === 'hitl_request' || m.type === 'hitl_response')
  const seenRequests = new Set<string>()
  return messages.filter((msg) => {
    const repeatsEarlierPrompt = isRepeatedRequest(msg, seenRequests)
    return shows(placeMessage(msg, { conversationHasHitl, repeatsEarlierPrompt }))
  })
}

/** Only canonical request or stored row identity establishes a replay (agion/Agion#3192). */
function isRepeatedRequest(msg: PlacedMessage, seenRequests: Set<string>): boolean {
  if (msg.type !== 'hitl_request') return false
  const requestId = msg.data['requestId']
  // Live requests carry requestId; plain assistant history carries only its
  // stored row id. Both already come from the server through the chat mapper.
  const rowId = 'id' in msg ? msg.id : undefined
  const key = typeof requestId === 'string' && requestId
    ? `request:${requestId}`
    : typeof rowId === 'string' && rowId ? `row:${rowId}` : undefined
  if (!key) return false
  if (seenRequests.has(key)) return true
  seenRequests.add(key)
  return false
}

/**
 * The list with the delivered message `id` moved after every message that came
 * before its delivery, at `deliveredAt` (phase 6, a message sent while the
 * agent works). The host writes its history row when the agent gets it, so a
 * reload lists it there too: the message shows where it was delivered, the same
 * live and on reload. A list without the message comes back as it was.
 */
export function placeAtDelivery<T extends { id: string; timestamp: number }>(
  messages: readonly T[],
  id: string,
  deliveredAt: number,
): readonly T[] {
  const at = messages.findIndex((m) => m.id === id)
  if (at === -1) return messages
  const rest = messages.filter((_, i) => i !== at)
  return [...rest, { ...messages[at]!, timestamp: deliveredAt }]
}

/**
 * Whether a message can show in the conversation in some context. A follow-up
 * run carries exactly these messages over; conversationMessages() then decides
 * with the whole conversation as context.
 */
export function mayShowInConversation(msg: PlacedMessage): boolean {
  return showsInConversation(placeMessage(msg, { conversationHasHitl: false, repeatsEarlierPrompt: false }))
}
